import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { RiArrowRightLine } from "@remixicon/react";

import { useRequestFeedback } from "@reflag/react-sdk";
import {
  FlagNote,
  ImportResponse,
  SegmentNote,
  SkippedSegment,
  SkipReason,
} from "@reflag/shared/importAPI";

type ImportPreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  importData: ImportResponse | null;
  onConfirmImport: () => void;
  isImporting: boolean;
};

function getSkipReasonLabel(reason: SkipReason): string {
  switch (reason) {
    case "multivariate":
      return "Multivariate flag (only boolean flags supported)";
    case "rules_serve_false":
      return "Has rules that explicitly serve false";
    case "already_exists":
      return "Already exists in this app";
    default:
      return reason;
  }
}

function getSkipReasonColor(reason: SkipReason): string {
  switch (reason) {
    case "multivariate":
      return "orange";
    case "rules_serve_false":
      return "yellow";
    case "already_exists":
      return "blue";
    default:
      return "gray";
  }
}

function getSegmentSkipReasonLabel(segment: SkippedSegment): string {
  switch (segment.reason) {
    case "already_exists":
      return "Already exists in this app";
    default:
      return segment.reason;
  }
}

function getSegmentNoteLabel(note: SegmentNote): string {
  switch (note) {
    case "rollout_not_supported":
      return "Skipping percentage-based rollouts rule. Not supported for segments";
    case "operator_approximated":
      return "Some operators approximated (e.g., startsWith → contains)";
    case "unknown_context_kind_ignored":
      return "Some context kinds were ignored (only user/company supported)";
    default:
      return note;
  }
}

function getFlagNoteLabel(note: FlagNote): string {
  switch (note) {
    case "operator_approximated":
      return "Some operators approximated (e.g., startsWith → contains)";
    case "prerequisite_not_found":
      return "Some prerequisite flags not found (may be multivariate)";
    case "unknown_context_kind_ignored":
      return "Some context kinds were ignored (only user/company supported)";
    default:
      return note;
  }
}

export function ImportPreviewModal({
  isOpen,
  onClose,
  importData,
  onConfirmImport,
  isImporting,
}: ImportPreviewModalProps) {
  const requestFeedback = useRequestFeedback();

  if (!importData) return null;

  const { flags, segments, skippedFlags, skippedSegments, environmentMapping } =
    importData.import;

  const hasSkippedFlags = skippedFlags.length > 0;
  const hasSkippedSegments = (skippedSegments?.length ?? 0) > 0;
  const hasImportableData = flags.length > 0 || segments.length > 0;
  const hasEnvironmentMapping = environmentMapping.length > 0;
  const flagsWithNotes = flags.filter((f) => f.notes && f.notes.length > 0);
  const segmentsWithNotes = segments.filter(
    (s) => s.notes && s.notes.length > 0,
  );

  return (
    <Modal isOpen={isOpen} size="2xl" trapFocus={false} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text>Import Preview</Text>
            <Badge colorScheme="purple" variant="subtle">
              Beta
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={6}>
            {/* Summary */}
            <Box>
              <Text fontWeight="medium" mb={3}>
                Ready to import:
              </Text>
              <HStack spacing={6}>
                <Flex align="center" gap={2}>
                  <Text fontSize="2xl" fontWeight="bold">
                    {flags.length}
                  </Text>
                  <Text color="dimmed">
                    {flags.length === 1 ? "flag" : "flags"}
                  </Text>
                </Flex>
                <Flex align="center" gap={2}>
                  <Text fontSize="2xl" fontWeight="bold">
                    {segments.length}
                  </Text>
                  <Text color="dimmed">
                    {segments.length === 1 ? "segment" : "segments"}
                  </Text>
                </Flex>
              </HStack>
            </Box>

            {/* Environment Mapping */}
            {hasEnvironmentMapping && (
              <Box>
                <Text fontWeight="medium" mb={2}>
                  Environment mapping
                </Text>
                <Box
                  borderColor="appBorder"
                  borderRadius="md"
                  borderWidth="1px"
                >
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>LaunchDarkly</Th>
                        <Th></Th>
                        <Th>Reflag</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {environmentMapping.map((mapping) => (
                        <Tr key={mapping.ldEnvName}>
                          <Td>
                            <Text fontWeight="medium">{mapping.ldEnvName}</Text>
                          </Td>
                          <Td>
                            <Icon as={RiArrowRightLine} color="dimmed" />
                          </Td>
                          <Td>
                            {mapping.reflagEnvName ? (
                              <Text fontWeight="medium">
                                {mapping.reflagEnvName}
                              </Text>
                            ) : (
                              <Text
                                color="dimmed"
                                fontSize="sm"
                                fontStyle="italic"
                              >
                                Will be created
                              </Text>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {environmentMapping.some((m) => !m.reflagEnvName) && (
                  <Text color="dimmed" fontSize="xs" mt={1}>
                    Missing environments will be created during import.
                  </Text>
                )}
              </Box>
            )}

            {/* Flags with notes */}
            {flagsWithNotes.length > 0 && (
              <Box>
                <Text fontWeight="medium" mb={2}>
                  {flagsWithNotes.length}{" "}
                  {flagsWithNotes.length === 1 ? "flag" : "flags"} with notes
                </Text>
                <Box
                  borderColor="appBorder"
                  borderRadius="md"
                  borderWidth="1px"
                >
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Flag</Th>
                        <Th>Note</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {flagsWithNotes.map((flag) => (
                        <Tr key={flag.flag.key}>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">{flag.flag.name}</Text>
                              <Text color="dimmed" fontSize="xs">
                                {flag.flag.key}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              {flag.notes?.map((note) => (
                                <Badge key={note} colorScheme="blue">
                                  {getFlagNoteLabel(note)}
                                </Badge>
                              ))}
                            </VStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* Segments with notes */}
            {segmentsWithNotes.length > 0 && (
              <Box>
                <Text fontWeight="medium" mb={2}>
                  {segmentsWithNotes.length}{" "}
                  {segmentsWithNotes.length === 1 ? "segment" : "segments"} with
                  notes
                </Text>
                <Box
                  borderColor="appBorder"
                  borderRadius="md"
                  borderWidth="1px"
                >
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Segment</Th>
                        <Th>Note</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {segmentsWithNotes.map((segment, index) => (
                        <Tr key={`segment-note-${index}`}>
                          <Td>
                            <Text fontWeight="medium">
                              {segment.segment.name}
                            </Text>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={1}>
                              {segment.notes?.map((note, noteIdx) => (
                                <Badge
                                  key={`${note}-${noteIdx}`}
                                  colorScheme="yellow"
                                >
                                  {getSegmentNoteLabel(note)}
                                </Badge>
                              ))}
                            </VStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* Skipped Flags */}
            {hasSkippedFlags && (
              <Box>
                <Text fontWeight="medium" mb={2}>
                  {skippedFlags.length}{" "}
                  {skippedFlags.length === 1 ? "flag" : "flags"} will be skipped
                </Text>
                <Box
                  borderColor="appBorder"
                  borderRadius="md"
                  borderWidth="1px"
                >
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Flag</Th>
                        <Th>Reason</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {skippedFlags.map((flag) => (
                        <Tr key={flag.key}>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="medium">{flag.name}</Text>
                              <Text color="dimmed" fontSize="xs">
                                {flag.key}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={getSkipReasonColor(flag.reason)}
                            >
                              {getSkipReasonLabel(flag.reason)}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* Skipped Segments */}
            {hasSkippedSegments && (
              <Box>
                <Text fontWeight="medium" mb={2}>
                  {skippedSegments!.length}{" "}
                  {skippedSegments!.length === 1 ? "segment" : "segments"} will
                  be skipped
                </Text>
                <Box
                  borderColor="appBorder"
                  borderRadius="md"
                  borderWidth="1px"
                >
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Segment</Th>
                        <Th>Reason</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {skippedSegments!.map((segment) => (
                        <Tr key={segment.name}>
                          <Td>
                            <Text fontWeight="medium">{segment.name}</Text>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">
                              {getSegmentSkipReasonLabel(segment)}
                            </Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {/* No importable data warning */}
            {!hasImportableData && (
              <Alert status="info">
                <AlertIcon />
                <Text>
                  No flags or segments are available to import. All flags may
                  have been skipped due to incompatible configurations.
                </Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            onClick={(e) => {
              requestFeedback({
                flagKey: "launchdarkly-import",
                title: "How is the LaunchDarkly import experience?",
                position: {
                  type: "POPOVER",
                  anchor: e.currentTarget,
                },
                openWithCommentVisible: true,
              });
            }}
          >
            Send feedback
          </Button>
          <Spacer />
          <Button mr={3} variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            isDisabled={!hasImportableData}
            isLoading={isImporting}
            loadingText="Importing..."
            onClick={onConfirmImport}
          >
            Import {flags.length} {flags.length === 1 ? "flag" : "flags"}
            {segments.length > 0 &&
              ` and ${segments.length} ${segments.length === 1 ? "segment" : "segments"}`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
